import React, { useState, useEffect, useRef } from 'react';
import { MessageSquare, Send, User, ChevronRight } from 'lucide-react';

export default function AdminInbox({ api, token, glassClass }) {
    const [conversations, setConversations] = useState([]);
    const [selectedChat, setSelectedChat] = useState(null);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const messagesEndRef = useRef(null);

    const loadConversations = async () => {
        try {
            const data = await api.fetch('/admin/messages', {}, token);
            setConversations(data || []);
        } catch (e) { console.error('Fout bij laden conversaties'); }
    };

    const loadMessages = async (userId) => {
        try {
            const data = await api.fetch(`/admin/messages/${userId}`, {}, token);
            setMessages(data || []);
            setConversations(prev => prev.map(c => c.id === userId ? { ...c, unread_count: 0 } : c));
        } catch (e) { console.error('Fout bij laden berichten'); }
    };

    useEffect(() => { loadConversations(); }, []);
    useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

    const handleSelectChat = (user) => {
        setSelectedChat(user);
        loadMessages(user.id);
    };

    const handleSend = async (e) => {
        e.preventDefault();
        if (!input.trim() || !selectedChat) return;
        const newMsg = { id: Date.now(), sender: 'coach', message: input.trim(), created_at: new Date() };
        setMessages(prev => [...prev, newMsg]);
        setInput('');
        setIsLoading(true);
        try {
            await api.fetch(`/admin/messages/${selectedChat.id}`, { method: 'POST', body: JSON.stringify({ message: newMsg.message }) }, token);
            loadMessages(selectedChat.id);
        } catch (e) { alert('Bericht niet verzonden'); }
        finally { setIsLoading(false); }
    };

    return (
        <div className={`${glassClass} flex flex-col md:flex-row h-[700px] max-h-[80vh] p-0 overflow-hidden`}>
            {/* Links: Atleten Lijst */}
            <div className="w-full md:w-1/3 border-b md:border-b-0 md:border-r border-zinc-700/50 bg-black/20 flex flex-col">
                <div className="p-4 border-b border-zinc-700/50 bg-black/40 flex items-center gap-2">
                    <MessageSquare size={20} className="text-orange-500"/>
                    <h2 className="text-lg font-bold">Actieve Chats</h2>
                </div>
                <div className="flex-1 overflow-y-auto custom-scrollbar">
                    {conversations.length === 0 ? (
                        <p className="p-4 text-sm text-gray-500 text-center">Nog geen inkomende berichten.</p>
                    ) : conversations.map(c => (
                        <button key={c.id} onClick={() => handleSelectChat(c)} className={`w-full text-left p-4 border-b border-zinc-800 hover:bg-white/5 transition-colors flex justify-between items-center ${selectedChat?.id === c.id ? 'bg-white/10 border-l-4 border-l-orange-500' : ''}`}>
                            <div>
                                <div className="font-bold text-gray-200 flex items-center gap-2"><User size={14}/> {c.name}</div>
                                {c.unread_count > 0 && <span className="text-[10px] bg-red-600 text-white px-2 py-0.5 rounded-full mt-1 inline-block uppercase tracking-wider">{c.unread_count} Nieuw</span>}
                            </div>
                            <ChevronRight size={16} className="text-gray-500"/>
                        </button>
                    ))}
                </div>
            </div>

            {/* Rechts: Het Chatvenster */}
            <div className="flex-1 flex flex-col bg-black/10">
                {selectedChat ? (
                    <>
                        <div className="p-4 border-b border-zinc-700/50 bg-black/40 flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-zinc-700 flex items-center justify-center border border-zinc-600"><User size={16}/></div>
                            <h3 className="font-bold text-lg">{selectedChat.name}</h3>
                        </div>
                        <div className="flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar">
                            {messages.map((msg, idx) => {
                                const isCoach = msg.sender === 'coach';
                                return (
                                    <div key={msg.id || idx} className={`flex ${isCoach ? 'justify-end' : 'justify-start'}`}>
                                        <div className={`max-w-[80%] p-3.5 rounded-2xl ${isCoach ? 'bg-orange-900/60 text-orange-50 rounded-tr-sm border border-orange-700/50 shadow-sm backdrop-blur-sm' : 'bg-zinc-800/80 text-gray-100 rounded-tl-sm border border-zinc-700/60 backdrop-blur-sm'}`}>
                                            <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                                            <div className={`text-[10px] mt-1 ${isCoach ? 'text-orange-200/50 text-right' : 'text-gray-400 text-left'}`}>
                                                {new Date(msg.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                            <div ref={messagesEndRef} />
                        </div>
                        <form onSubmit={handleSend} className="p-4 bg-zinc-900 border-t border-zinc-800">
                            <div className="relative flex items-center">
                                <input type="text" value={input} onChange={(e) => setInput(e.target.value)} placeholder={`Typ een antwoord aan ${selectedChat.name}...`} disabled={isLoading} className="w-full bg-black/50 border border-zinc-700 rounded-full pl-5 pr-14 py-3 text-sm text-white focus:outline-none focus:border-orange-500 transition-colors disabled:opacity-50" />
                                <button type="submit" disabled={!input.trim() || isLoading} className="absolute right-2 p-2 bg-orange-600 hover:bg-orange-500 disabled:bg-zinc-700 rounded-full transition-colors"><Send size={16} /></button>
                            </div>
                        </form>
                    </>
                ) : (
                    <div className="flex-1 flex items-center justify-center text-gray-500 flex-col gap-4">
                        <MessageSquare size={48} className="opacity-20" />
                        <p>Selecteer links een atleet om de chat te openen</p>
                    </div>
                )}
            </div>
        </div>
    );
}
